import React from "react"
import Tag from "../Tag/Tag"
import useAccordionList from "../../hooks/useAccordionList"
import {
	DataTable,
	DataTableActionHeader,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableExpandableRow,
	DataTableHead,
	DataTableRow,
} from "../DataTable/DataTable"
import {
	AdverseMediaMatches,
	AdverseMediaMatchesType,
	EvaluationForName,
	Pep,
	Sanctions,
	WatchlistMatchesData,
	WatchlistMatchesTypes,
} from "../../resources/application"

export interface Props {
	pep?: Pep[]
	sanctions?: Sanctions[]
	adverseMediaMatches?: AdverseMediaMatches[]
}

const urlPattern = /^https?:\/\/|^\/\//i

function getListLength<T>(list?: T[]) {
	return list?.length || 0
}

function getItemsData(watchlistMatchesData: WatchlistMatchesData, index: number) {
	let name: string
	let attributesData: {[key: string]: string[]} | undefined
	let adverseMediaItems: AdverseMediaMatchesType[] | undefined

	switch (watchlistMatchesData.type) {
		case WatchlistMatchesTypes.Sanctions:
			name =
				watchlistMatchesData.data?.[index].attributes.name?.[0] ||
				watchlistMatchesData.data?.[index].attributes.entity_name?.[0] ||
				""
			attributesData = watchlistMatchesData.data?.[index].attributes
			break
		case WatchlistMatchesTypes.Pep:
			name = watchlistMatchesData.data?.[index].attributes.name[0] || ""
			attributesData = watchlistMatchesData.data?.[index].attributes
			break
		case WatchlistMatchesTypes.AdverseMediaMatches:
			name = watchlistMatchesData.data?.[index].matchItems[0].entityName || ""
			adverseMediaItems = watchlistMatchesData.data?.[index].matchItems
			break
	}
	return {name, attributesData, adverseMediaItems}
}

function sortMatchesByForNameInternal<T extends EvaluationForName>(
	watchListMatchesType: T[] | undefined
): T[] | undefined {
	return watchListMatchesType?.sort(
		(a, b) => (a.forNames?.[0].name || "").localeCompare(b.forNames?.[0].name || "") || 0
	)
}

/** @TODO - improve code readability */
export default function WatchlistMatches({pep, sanctions, adverseMediaMatches}: Props) {
	const numOfItems = getListLength(pep) + getListLength(sanctions) + getListLength(adverseMediaMatches)

	const {handleExpand, expandedIndices} = useAccordionList({numOfItems, allowMultipleExpanded: false})

	const watchlistMatchesData: WatchlistMatchesData[] = [
		{type: WatchlistMatchesTypes.AdverseMediaMatches, data: sortMatchesByForNameInternal(adverseMediaMatches)},
		{type: WatchlistMatchesTypes.Pep, data: sortMatchesByForNameInternal(pep)},
		{type: WatchlistMatchesTypes.Sanctions, data: sortMatchesByForNameInternal(sanctions)},
	]

	let rowIndex = 0

	return (
		<DataTableCard className="watchlist-matches">
			<DataTableActionHeader title="Watchlist Matches" />
			<DataTable fullHeight={false} hideMinHeight>
				<DataTableHead>
					<DataTableRow>
						<DataTableCell>Entity name</DataTableCell>
						<DataTableCell>Type</DataTableCell>
						<DataTableCell>For</DataTableCell>
						<DataTableCell>Match fields</DataTableCell>
						<DataTableCell>Strength</DataTableCell>
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{watchlistMatchesData.map((watchlistMatchData, i) =>
						watchlistMatchData.data?.map((watchlistMatch, j) => {
							const matchFields = watchlistMatch.attributes?.["matchFields"] ?? []
							const currentRowIndex = rowIndex++ // Use currentRowIndex and then increment rowIndex
							const {name, attributesData, adverseMediaItems} = getItemsData(watchlistMatchData, j)
							const forNameColumnData = watchlistMatch.forNames?.map((forName) => forName.name) || ["-"]

							return (
								<DataTableExpandableRow
									key={`${watchlistMatchData.type}-${i}-${j}`}
									onExpand={() => handleExpand(currentRowIndex)}
									expanded={expandedIndices[currentRowIndex]}
									cells={[
										name || "",
										watchlistMatchData.type,
										forNameColumnData.join(", "),
										<div key="matchFields" className="truncate-two-lines" title={matchFields.join(", ")}>
											{matchFields.join(", ") || "-"}
										</div>,
										<Tag key="score" content={watchlistMatch.score} variant="outline" theme="neutral" size="small" />,
									]}
								>
									<div className="list-wrapper">
										{[WatchlistMatchesTypes.Sanctions, WatchlistMatchesTypes.Pep].includes(watchlistMatchData.type) &&
											attributesData && (
												<dl data-type={watchlistMatchData.type}>
													{Object.entries(attributesData).map(([key, value]) => {
														const isValidUrl = urlPattern.test(value[0])
														const updatedKey = key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()
														let updatedValue = <>{value.join(", ")}</>
														if (isValidUrl) {
															updatedValue = (
																<a href={value[0]} target="_blank" rel="noopener noreferrer">
																	{value}
																</a>
															)
														}
														return (
															<div key={key} data-key={key}>
																<dt>{updatedKey}</dt>
																<dd>{updatedValue}</dd>
															</div>
														)
													})}
												</dl>
											)}

										{watchlistMatchData.type === WatchlistMatchesTypes.AdverseMediaMatches &&
											adverseMediaItems?.map((item, index) => (
												<React.Fragment key={index}>
													<dl data-type={watchlistMatchData.type}>
														{Object.entries(item).map(([key, value]) => {
															const isValidUrl = urlPattern.test(value)
															let updatedValue = <>{value}</>
															if (isValidUrl) {
																updatedValue = (
																	<a href={value} target="_blank" rel="noopener noreferrer">
																		{value}
																	</a>
																)
															}
															return (
																<div key={key} data-key={key}>
																	<dt>{key}</dt>
																	<dd>{updatedValue}</dd>
																</div>
															)
														})}
													</dl>
													{adverseMediaItems && index < adverseMediaItems.length && <hr />}
												</React.Fragment>
											))}
									</div>
								</DataTableExpandableRow>
							)
						})
					)}
				</DataTableBody>
			</DataTable>
		</DataTableCard>
	)
}
